import React, { useState, useEffect } from "react";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Checkbox, Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { DateFormatCell } from "components/common/formatter";
import { process } from "@progress/kendo-data-query";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton";
import {
    getActiveInterestSchemes,
    getInterestCalculation,
} from "services/interestCalculatorService";
import MattersCombobox from "pages/Postings/MattersComboBox";
import { Loader } from "@progress/kendo-react-indicators";
import { GRID_PAGE_SIZES } from "components/common/GridComponents";

const DetailComponent = (props) => {
    const details = props.dataItem.detailedCalculations || [];
    if (details.length === 0) {
        return <p className="ms-3 my-2">No detailed calculations available.</p>;
    }
    return (
        <Grid data={details} className="fs-md">
            <GridColumn field="fromDate" title="From" cell={DateFormatCell} width="130px" />
            <GridColumn field="toDate" title="To" cell={DateFormatCell} width="130px" />
            <GridColumn field="amount" title="Amount" width="120px" className="text-end" />
            <GridColumn field="days" title="Days" width="90px" className="text-center" />
            <GridColumn field="interestRate" title="Rate %" width="100px" className="text-end pe-4" />
            <GridColumn field="interestAmount" title="Interest" width="100px" className="text-end pe-4" />
        </Grid>
    );
};

const MatterCalculation = () => { 
    const [useFromDate, setUseFromDate] = useState(false);
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const [selectedMatter, setSelectedMatter] = useState(null);
    const [showDetailed, setShowDetailed] = useState(false);
    const [onlyWithScheme, setOnlyWithScheme] = useState(false);
    const [schemes, setSchemes] = useState([]);
    const [selectedScheme, setSelectedScheme] = useState(null);
    const [gridData, setGridData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingSchemes, setLoadingSchemes] = useState(false);
    const [dataState, setDataState] = useState({
        take: 50,
        skip: 0,
        sort: [{ field: "matterRef", dir: "asc" }],
    });
    const [dataResult, setDataResult] = useState(process([], dataState));

    useEffect(() => {
        const fetchSchemes = async () => {
            setLoadingSchemes(true);
            try {
                const schemesData = await getActiveInterestSchemes();
                setSchemes(schemesData || []);
                if (schemesData?.length > 0) {
                    setSelectedScheme(schemesData[0]);
                }
            } catch (error) {
                console.error("Failed to fetch interest schemes", error);
            } finally {
                setLoadingSchemes(false);
            }
        };
        fetchSchemes();
    }, []);

    const handleCalculate = async () => {
        setLoading(true);
        const params = {
            ToDate: toDate.toISOString().split("T")[0],
            ShowDetailedCalculation: showDetailed,
            DefaultInterestSchemeId: selectedScheme?.interestSchemeId,
            ...(useFromDate && { FromDate: fromDate.toISOString().split("T")[0] }),
            ...(selectedMatter && { MatterId: selectedMatter.matterId }),
            ...(!selectedMatter && { OnlyMattersWithScheme: onlyWithScheme }),
        };
        try {
            const result = await getInterestCalculation(params);
            const data = result || [];
            setGridData(data);
            setDataResult(process(data, dataState));
        } catch (error) {
            console.error("Failed to calculate interest", error);
            setGridData([]);
            setDataResult({ data: [], total: 0 });
        } finally {
            setLoading(false);
        }
    };

    const handleDataStateChange = (e) => {
        setDataState(e.dataState);
        setDataResult(process(gridData, e.dataState));
    };

    const handleExpandChange = (e) => {
        const newData = gridData.map((item) =>
            item === e.dataItem ? { ...item, expanded: !e.dataItem.expanded } : item
        );
        setGridData(newData);
        setDataResult(process(newData, dataState));
    };

    return (
        <div className="h-100 d-flex flex-column">

            <div className="d-flex flex-wrap align-items-end gap-3">
                <div>
                    <div>
                        <Label editorId={"from"} className="me-5 pe-5 mb-1">
                            From
                        </Label>
                        <Label editorId={"to"} className="ms-4 ps-1 mb-1">
                            To
                        </Label>
                    </div>
                    <div className="date-form-to-wrapper">
                        <Checkbox
                            className="ms-2 mt-auto mb-auto"
                            value={useFromDate}
                            onChange={() => setUseFromDate(!useFromDate)}
                        />
                        <div className="from">
                            <DatePicker
                                id="from"
                                className="date-picker pe-2"
                                value={fromDate}
                                onChange={(e) => { setFromDate(e.value); setUseFromDate(true) }}
                                toggleButton={CalenderCustomToggleButton}
                            />
                        </div>
                        <div className="divider m-1"></div>

                        <div className="to">
                            <DatePicker
                                id="to"
                                className="date-picker pe-2"
                                value={toDate}
                                onChange={(e) => setToDate(e.value)}
                                toggleButton={CalenderCustomToggleButton}
                            />
                        </div>
                    </div>
                </div>
                <div className="d-flex flex-column gap-1">
                    <Label>Matter Ref</Label>
                    <MattersCombobox
                        onChangematter={(e) => setSelectedMatter(e.value)}
                        value={selectedMatter}
                        style={{ width: "250px", height: "35px" }}
                    />
                </div>

                <div className="d-flex flex-column gap-1">
                    <Checkbox
                        label="Show Detailed Calculation"
                        className="fs-md fw-semibold"
                        labelClassName="fs-md fw-semibold ms-2"
                        checked={showDetailed}
                        onChange={() => setShowDetailed(!showDetailed)}
                    />
                    <Checkbox
                        label="Only Matters with Interest Scheme"
                        className="fs-md fw-semibold"
                        labelClassName="fs-md fw-semibold ms-2"
                        checked={onlyWithScheme}
                        onChange={() => setOnlyWithScheme(!onlyWithScheme)}
                        disabled={!!selectedMatter}
                    />
                </div>
                <div className=" d-flex flex-column gap-1 me-auto">
                    <Label>Default Interest Scheme</Label>
                    <ComboBox
                        iconClassName="wa"
                        style={{ width: "250px", height: "35px" }}
                        data={schemes}
                        textField="schemeName"
                        dataItemKey="interestSchemeId"
                        value={selectedScheme}
                        onChange={(e) => setSelectedScheme(e.value)}
                        loading={loadingSchemes}
                    />
                </div>
                <Button
                    className="common-btn "
                    onClick={handleCalculate}
                    disabled={loading}
                >
                    {loading ? (
                        <Loader
                            size="small"
                            type="infinite-spinner"
                            className="text-white"
                        />
                    ) : (
                        "Calculate"
                    )}
                </Button>
            </div>


            <Grid
                data={dataResult}
                className="mt-3 fs-md flex-grow-1 overflow-auto grid-minwidth-col2-200"

                {...dataState}
                onDataStateChange={handleDataStateChange}
                pageable={{
                    pageSizes: GRID_PAGE_SIZES,
                }}
                sortable={true}
                detail={showDetailed ? DetailComponent : undefined}
                expandField="expanded"
                onExpandChange={handleExpandChange}
            >
                <GridColumn field="matterRef" title="Matter" width="120px" />
                <GridColumn field="matterDetails" title="Matter Details" />
                <GridColumn
                    field="fromDate"
                    title="From"
                    cell={DateFormatCell}
                    width="110px"
                />
                <GridColumn field="toDate" title="To" cell={DateFormatCell} width="110px" />
                <GridColumn field="days" title="Days" width="90px" className="text-center" />
                <GridColumn
                    field="interestRate"
                    title="Rate %"
                    width="100px"
                    className="text-end pe-4" />

                <GridColumn
                    field="interestAmount"
                    title="Interest"
                    width="110px"
                    className="text-end pe-4" />

                <GridColumn field="previousInterest" title="Previous Interest" width="150px" />
                <GridColumn
                    field="select"
                    title="Select"
                    width="80px"
                    cell={(props) => (
                        <td style={{ textAlign: "center" }}>
                            <Checkbox
                            // onChange logic to be added
                            />
                        </td>
                    )}
                />
            </Grid>


            <div className="d-flex justify-content-end mt-2 ms-auto">
                <Button className="common-btn" disabled>
                    Post
                </Button>
            </div>
        </div>
    );
};

export default MatterCalculation;
